import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AddFundsModal from "../margin/AddFundsModal";
import { FuturesTradeTransferProvider, useFuturesTradeTransfer } from "../futures_trade/FuturesTradeTransferContext";
import StakingService from "../../../api/services/StakingService";
import { normalizeIconPathString, findFundRowBySymbol } from "../WithdrawPage/withdrawPageHelpers";
import StakeModal from "./StakeModal";
import { Helmet } from "react-helmet-async";
import { getPackageIcon, getRewardIcon, getPackageCurrencyId, resolveStakeWalletBalance } from "./stakingStakeHelpers";
import Footer from "../../../customComponents/Footer";
import './staking.css';


const STAKING_TYPE_LABELS = {
    LOCKED: "Locked Staking",
    FLEXIBLE: "Flexible Staking",
    TOKENIZED: "Tokenized Staking",
};

const formatApr = (pkg) => {
    const min = pkg?.aprMin;
    const max = pkg?.aprMax ?? pkg?.returnPercentage;
    if (min != null && max != null && min !== max) {
        return `${min}% - ${max}%`;
    }
    if (max != null) return `${max}%`;
    return "—";
};

const getTagBadgeClass = (tag) => {
    const t = String(tag || "").toLowerCase();
    if (t === "vip") return "badge_vip";
    if (t === "new") return "badge_new";
    return "badge_bonus";
};

const readAuthToken = () => {
    try {
        return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    } catch {
        return "";
    }
};

const Staking = () => {
    const navigate = useNavigate();
    const { openWalletTransfer } = useFuturesTradeTransfer() || {};
    const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(readAuthToken()));
    const [activeWhyStake, setActiveWhyStake] = useState(0);
    const [openFaq, setOpenFaq] = useState(null);
    const [mobilePopupRow, setMobilePopupRow] = useState(null);
    const [addFundsOpen, setAddFundsOpen] = useState(false);
    const [showMintModal, setShowMintModal] = useState(false);
    const [showIntroModal, setShowIntroModal] = useState(false);
    const [isFastRedemption, setIsFastRedemption] = useState(false);
    const [mintAmount, setMintAmount] = useState("");
    const [mintChecked, setMintChecked] = useState(false);
    const [mintCoin, setMintCoin] = useState("AGCX");

    const [showPlanModal, setShowPlanModal] = useState(false);
    const [planModalCurrency, setPlanModalCurrency] = useState("");
    const [planModalCurrencyFullName, setPlanModalCurrencyFullName] = useState("");
    const [planModalCurrencyIcon, setPlanModalCurrencyIcon] = useState("");
    const [planModalPackages, setPlanModalPackages] = useState([]);
    const [planModalLoading, setPlanModalLoading] = useState(false);

    const [showStakeModal, setShowStakeModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const pageLimit = 10;

    /** symbol/currencyId -> Available balance, prefetched on page load so the Stake modal shows it instantly. */
    const balanceCacheRef = useRef(new Map());

    const prefetchStakeBalances = useCallback(async (pkgs) => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token || !Array.isArray(pkgs) || pkgs.length === 0) return;

        // One unique entry per currency: symbol -> currencyId (if known on the package).
        const bySymbol = new Map();
        pkgs.forEach((pkg) => {
            const sym = String(pkg?.currency || "").toUpperCase();
            if (!sym) return;
            if (!bySymbol.has(sym)) bySymbol.set(sym, getPackageCurrencyId(pkg));
        });

        // Resolve any missing currencyIds from the earning wallet in a single call.
        if (Array.from(bySymbol.values()).some((id) => id == null)) {
            try {
                const walletRes = await StakingService.getEarningWallet();
                if (walletRes?.success && Array.isArray(walletRes.data)) {
                    bySymbol.forEach((id, sym) => {
                        if (id != null) return;
                        const row = findFundRowBySymbol(walletRes.data, sym);
                        const cid = row?.currency_id ?? row?.currencyId ?? null;
                        if (cid != null) bySymbol.set(sym, cid);
                    });
                }
            } catch {
                /* ignore — modal will fetch on open as a fallback */
            }
        }

        await Promise.all(
            Array.from(bySymbol.entries()).map(async ([sym, currencyId]) => {
                if (currencyId == null) return;
                try {
                    const res = await StakingService.getUserBalance(currencyId, "earning");
                    const bal = resolveStakeWalletBalance(res);
                    balanceCacheRef.current.set(sym, bal);
                    balanceCacheRef.current.set(String(currencyId), bal);
                } catch {
                    /* ignore */
                }
            }),
        );
    }, []);

    const fetchPackages = useCallback(async (page = 1) => {
        setPackagesLoading(true);
        const res = await StakingService.getPackages(page, pageLimit);
        if (res?.success && Array.isArray(res.data)) {
            const lockedPackages = res.data.filter(
                (pkg) => String(pkg?.stakingType || "").toUpperCase() === "LOCKED" && pkg?.status === "ACTIVE"
            );
            setPackages(lockedPackages);
            if (res.pagination) {
                setPagination((prev) => ({ ...prev, ...res.pagination, page }));
            }
        } else {
            setPackages([]);
        }
        setPackagesLoading(false);
    }, []);

    useEffect(() => {
        fetchPackages(1);
    }, [fetchPackages]);

    useEffect(() => {
        if (!packagesLoading && packages.length > 0) {
            prefetchStakeBalances(packages);
        }
    }, [packagesLoading, packages, prefetchStakeBalances]);

    const filteredPackages = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = q
            ? packages.filter((pkg) => {
                const currency = String(pkg?.currency || "").toLowerCase();
                const fullName = String(pkg?.currencyFullName || "").toLowerCase();
                return currency.includes(q) || fullName.includes(q);
            })
            : packages;

        // deduplicate by currency — one row per coin
        const seen = new Set();
        return filtered.filter((pkg) => {
            const key = String(pkg?.currency || "").toUpperCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [packages, searchQuery]);

    useEffect(() => {
        const syncAuth = () => setIsLoggedIn(Boolean(readAuthToken()));
        window.addEventListener("agce-auth-changed", syncAuth);
        window.addEventListener("agce-auth-token-changed", syncAuth);
        window.addEventListener("storage", syncAuth);
        return () => {
            window.removeEventListener("agce-auth-changed", syncAuth);
            window.removeEventListener("agce-auth-token-changed", syncAuth);
            window.removeEventListener("storage", syncAuth);
        };
    }, []);

    const handleStakeClick = (pkg) => {
        if (!isLoggedIn) {
            navigate("/login");
            return;
        }
        openPlanModal(pkg);
    };

    const openPlanModal = async (pkg) => {
        setPlanModalCurrency(pkg.currency);
        setPlanModalCurrencyFullName(pkg.currencyFullName || "");
        setPlanModalCurrencyIcon(pkg.iconPath);

        setPlanModalPackages([]);
        setPlanModalLoading(true);
        setShowPlanModal(true);
        const res = await StakingService.getPackagesForCoin(pkg.currency);
        if (res?.success && Array.isArray(res.data)) {
            setPlanModalPackages(res.data);
        }
        setPlanModalLoading(false);
    };

    const openStakeModal = (pkg) => {
        setSelectedPackage(pkg);
        setShowStakeModal(true);
    };

    const selectPlanAndStake = (plan) => {
        setShowPlanModal(false);
        openStakeModal({
            ...plan,
            currency: plan.currency || planModalCurrency,
            currencyFullName: plan.currencyFullName || planModalCurrencyFullName || "",
        });
    };

    const openPackagePopup = (pkg) => {
        openPopup({
            coin: pkg.currency,
            type: STAKING_TYPE_LABELS[pkg.stakingType] || pkg.stakingType,
            action: "Stake",
            rewards: [getRewardIcon(pkg)],
            apr: formatApr(pkg),
            package: pkg,
        });
    };

    useEffect(() => {
        const isAnyModalOpen = mobilePopupRow || addFundsOpen || showMintModal || showIntroModal || showStakeModal || showPlanModal;
        if (isAnyModalOpen) {
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        };
    }, [mobilePopupRow, addFundsOpen, showMintModal, showIntroModal, showStakeModal]);

    const openPopup = (data) => {
        if (window.innerWidth <= 767) {
            setMobilePopupRow(data);
        }
    };

    const toggleFaq = (i) => {
        setOpenFaq((v) => (v === i ? null : i));
    };

    const STAKING_FAQ_ITEMS = [
        {
            question: "What is Staking?",
            answer: "Staking is the process of locking up cryptocurrency assets to participate in transaction validation on a Proof-of-Stake (PoS) blockchain. In return for securing the network, participants earn staking rewards."
        },
        {
            question: "What is Proof of Stake (PoS)?",
            answer: "Proof of Stake (PoS) is a consensus mechanism used by blockchains to agree on the validity of transactions. Instead of using computing power like Proof of Work (mining), PoS relies on users who 'stake' their tokens to secure the network."
        },
        {
            question: "When will I receive my staking return?",
            answer: "Staking returns are typically calculated daily and distributed according to the rules of the specific product. Depending on the asset, rewards may be credited to your account daily or at the end of a fixed staking period."
        },
        {
            question: "Why choose AGCX Staking?",
            answer: "AGCX Staking offers a safe and seamless experience with competitive yields across a wide pool of top PoS products. We provide a low threshold for entry, 100% Proof of Reserve security, and a flexible redemption process."
        },
        {
            question: "How is the Est. APR calculated?",
            answer: "The Estimated Annual Percentage Rate (APR) is dynamically calculated based on the underlying blockchain's on-chain reward rates, total network participation, and platform conditions. The actual rate may fluctuate over time."
        }
    ];

    const whyStakeData = [
        {
            title: "Pool of Top PoS Products",
            desc: "All of the best PoS products recognized in the industry are available at AGCX"
        },
        {
            title: "Low Threshold and High Return",
            desc: "Start staking with minimal requirements and earn competitive returns."
        },
        {
            title: "100% Proof of Reserve",
            desc: "Your assets are fully backed and verified on-chain for complete transparency."
        }
    ];

    return (


        <>
            <Helmet>
                <title>Crypto Staking | Earn Rewards on Your Holdings – AGCX</title>
                <meta
                    name="description"
                    content="Stake your crypto on AGCX and earn passive rewards. Flexible and fixed staking options with competitive APY, available to UAE and global users."
                />
                <meta name="keywords" content="crypto staking platform, earn APY on crypto, staking rewards UAE" />
                <link rel="canonical" href="https://arabglobal.ae/staking" />
            </Helmet>



            <div className="staking_page_wrapper">
                <div className="container">
                    <div className='staking_bnr_cnt d-flex'>
                        <div className='staking_bnr_left'>
                            <h1 className="staking_title">Staking</h1>
                            <p className="staking_desc">Put your crypto to work and earn rewards.</p>
                            <div className='staking_btn d-flex gap-2'>
                                {/* <button className='loginbtn'>Login</button> */}
                                {/* <button onClick={() => navigate('/staking-detail')}>About Staking</button> */}
                            </div>
                        </div>
                        <div className='staking_img'>
                            <img className='dark_img' src="/images/staking_img/staking_bnr_img2.svg" alt="Why Stake Graphic" />
                            <img className='light_img' src="/images/staking_img/staking_bnr_img2_light.svg" alt="Why Stake Graphic" />
                        </div>
                    </div>



                    <div className="staking_products_section">
                        <div className="staking_products_header stakinghdr2">
                            <h2>All Products</h2>
                            <div className="staking_products_filters">
                                <label className="staking_checkbox_label">
                                    <input type="checkbox" />
                                    <span className="checkmark"></span>
                                    Match my assets
                                </label>
                                <div className="searchBar custom-tabs">
                                    <i className="ri-search-2-line"></i>
                                    <input
                                        type="search"
                                        className="custom_search"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>


                        <div className="staking_table_container">
                            <table className="staking_table">
                                <thead>
                                    <tr>
                                        <th>Coin</th>
                                        <th>Est. APR <span className="sort_icon">⬍</span></th>
                                        <th>Reward Coin</th>
                                        <th>Type</th>
                                        <th className="action_th">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {packagesLoading ? (
                                        <tr>
                                            <td colSpan={5} className="mint-table-empty">
                                                Loading packages...
                                            </td>
                                        </tr>
                                    ) : filteredPackages.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="mint-table-empty">
                                                No locked staking packages available.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredPackages.map((pkg) => {
                                            const coinIcon = getPackageIcon(pkg);
                                            const rewardIcon = getRewardIcon(pkg);
                                            const typeLabel = STAKING_TYPE_LABELS[pkg.stakingType] || pkg.stakingType;

                                            return (
                                                <tr key={pkg._id || pkg.packageId} onClick={() => openPackagePopup(pkg)}>
                                                    <td>
                                                        <div className="coin_cell">
                                                            <div className="coin_icon_stak">
                                                                <img
                                                                    src={coinIcon}
                                                                    alt={pkg.currency}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = `/images/staking_img/${String(pkg.currency || "usdt").toLowerCase()}_icon.svg`;
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="coin_name">{pkg.currency}</span>
                                                            {pkg.tag ? (
                                                                <span className={`coin_badge ${getTagBadgeClass(pkg.tag)}`}>{pkg.tag}</span>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                    <td className="apr_cell">{formatApr(pkg)}</td>
                                                    <td>
                                                        <div className="reward_coin_cell">
                                                            <div className="reward_icon">
                                                                <img
                                                                    src={rewardIcon}
                                                                    alt={pkg.rewardCurrency}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = `/images/staking_img/${String(pkg.rewardCurrency || pkg.currency || "usdt").toLowerCase()}_icon.svg`;
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="type_cell">{typeLabel}</td>
                                                    <td className="action_td">
                                                        <button
                                                            className="action_btn mint-cards-wrapper"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleStakeClick(pkg);
                                                            }}
                                                        >
                                                            {isLoggedIn ? "Stake" : "Stake"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination.totalPages > 1 ? (
                            <div className="staking_pagination">
                                <button
                                    type="button"
                                    className="page_nav prev_nav"
                                    disabled={pagination.page <= 1}
                                    onClick={() => fetchPackages(pagination.page - 1)}
                                >
                                    ‹
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                                    <button
                                        key={pageNum}
                                        type="button"
                                        className={`page_num${pagination.page === pageNum ? " active" : ""}`}
                                        onClick={() => fetchPackages(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="page_nav next_nav"
                                    disabled={pagination.page >= pagination.totalPages}
                                    onClick={() => fetchPackages(pagination.page + 1)}
                                >
                                    ›
                                </button>
                            </div>
                        ) : null}
                    </div>

                    <div className="staking_info_section">
                        <h2 className="staking_info_title">What is crypto staking?</h2>
                        <div className="staking_info_cards">
                            <div className="staking_info_card">
                                <div className='number'>01</div>
                                <div className="staking_info_icon">
                                    <img src="/images/staking_img/crypto_staking_icon.svg" alt="Crypto Staking" />
                                </div>
                                <h3>Deposit Your Assets</h3>
                                <p>Choose your asset and stake with just a few clicks.</p>

                                <div className='nextarrow_step'>
                                    <i class="ri-arrow-right-long-line"></i>
                                </div>

                            </div>
                            <div className="staking_info_card">
                                <div className='number'>02</div>
                                <div className="staking_info_icon">
                                    <img src="/images/staking_img/crypto_staking_icon2.svg" alt="Secure The Network" />
                                </div>
                                <h3>Secure The Network</h3>
                                <p>Your stake helps validate transactions and keep the network secure.</p>

                                <div className='nextarrow_step'>
                                    <i class="ri-arrow-right-long-line"></i>
                                </div>

                            </div>
                            <div className="staking_info_card">
                                <div className='number'>03</div>
                                <div className="staking_info_icon">
                                    <img src="/images/staking_img/crypto_staking_icon3.svg" alt="Earn Rewards" />
                                </div>
                                <h3>Earn Rewards</h3>
                                <p>Get rewarded in real-time proportional to your contribution.</p>
                            </div>
                        </div>
                    </div>


                    {/* Why stake on AGCX? */}
                    <div className="why_stake_section_new">
                        <div className="why_stake_left">
                            <h2 className="staking_info_title">Why stake on AGCX?</h2>
                            <div className="why_stake_cards">
                                {whyStakeData.map((item, index) => (
                                    <div key={index} className="why_stake_card">
                                        <div className="why_stake_card_icon">
                                            <img src={`/images/staking_img/stake_acge_icon${index === 0 ? '' : index + 1}.svg`} alt={item.title} />
                                        </div>
                                        <div className="why_stake_card_content">
                                            <h3>{item.title}</h3>
                                            <p>{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="why_stake_right">
                            <img className='dark_img' src="/images/staking_img/stake_crypto.svg" alt="Why Stake Graphic" />
                            <img className='light_img' src="/images/staking_img/stake_crypto_light.svg" alt="Why Stake Graphic" />
                        </div>
                    </div>

                    {/* How to stake on AGCX? */}
                    <div className="how_stake_section">
                        <h2 className="staking_info_title">How to stake on AGCX ?</h2>
                        <div className="how_stake_timeline">
                            <div className="timeline_line"></div>
                            <div className="timeline_step">
                                <div className="step_icon_container">
                                    <div className="step_number">1</div>
                                    <div className="step_icon"><img src="/images/staking_img/wallet_icon.svg" alt="Connect Wallet" /></div>
                                </div>
                                <h3>Connect Wallet</h3>
                                <p>Securely connect your wallet to get started.</p>
                            </div>
                            <div className="timeline_step">
                                <div className="step_icon_container">
                                    <div className="step_number">2</div>
                                    <div className="step_icon"><img src="/images/staking_img/assets_icon.svg" alt="Choose Asset" /></div>
                                </div>
                                <h3>Choose Asset</h3>
                                <p>Select the asset you want to stake.</p>
                            </div>
                            <div className="timeline_step">
                                <div className="step_icon_container">
                                    <div className="step_number">3</div>
                                    <div className="step_icon"><img src="/images/staking_img/staking_amount_icon.svg" alt="Start Staking" /></div>
                                </div>
                                <h3>Start Staking</h3>
                                <p>Enter amount and confirm your staking.</p>
                            </div>
                            <div className="timeline_step">
                                <div className="step_icon_container">
                                    <div className="step_number">4</div>
                                    <div className="step_icon"><img src="/images/staking_img/remards_icon.svg" alt="Earn Rewards" /></div>
                                </div>
                                <h3>Earn Rewards</h3>
                                <p>Watch your rewards grow in real-time.</p>
                            </div>
                            <div className="timeline_step">
                                <div className="step_icon_container">
                                    <div className="step_number">5</div>
                                    <div className="step_icon"><img src="/images/staking_img/withdraw_icon.svg" alt="Withdraw Anytime" /></div>
                                </div>
                                <h3>Withdraw Anytime</h3>
                                <p>Claim rewards or unstake whenever you want.</p>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <section className="convert-page__faqSection" aria-labelledby="convert-page-faq-title">
                        <h2 id="convert-page-faq-title" className="convert-page__faqTitle">
                            FAQ
                        </h2>
                        <ul className="convert-page__faqList">
                            {STAKING_FAQ_ITEMS.map((item, i) => (
                                <li key={item.question} className="convert-page__faqItem">
                                    <button type="button" className="convert-page__faqBtn" onClick={() => toggleFaq(i)} aria-expanded={openFaq === i}>
                                        <span>{item.question}</span>
                                        <i className={`ri-arrow-down-s-line convert-page__faqChev${openFaq === i ? " convert-page__faqChev--open" : ""}`} aria-hidden />
                                    </button>
                                    {openFaq === i ? (
                                        <div className="convert-page__faqPanel" role="region">
                                            <p>{item.answer}</p>
                                        </div>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </section>
                    {mobilePopupRow && (
                        <div className="mobile_staking_popup_overlay" onClick={() => setMobilePopupRow(null)}>
                            <div className="mobile_staking_popup" onClick={(e) => e.stopPropagation()}>
                                <div className="popup_header">
                                    <h3>{mobilePopupRow.coin}</h3>
                                    <button className="close_btn" onClick={() => setMobilePopupRow(null)}>&times;</button>
                                </div>
                                <div className="popup_body">
                                    <div className="popup_row">
                                        <span>Est. APR</span>
                                        <span className="apr_val">{mobilePopupRow.apr}</span>
                                    </div>
                                    <div className="popup_row">
                                        <span>Reward Coin</span>
                                        <div className="reward_popup_icons">
                                            {mobilePopupRow.rewards && mobilePopupRow.rewards.map((r, idx) => (
                                                <img key={idx} src={r} alt="Reward" className="reward_popup_icon" />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="popup_row">
                                        <span>Type</span>
                                        <span>{mobilePopupRow.type}</span>
                                    </div>
                                </div>
                                <div className="popup_footer">
                                    <button
                                        className="action_btn loginbtn"
                                        style={{ width: '100%', padding: '12px' }}
                                        onClick={() => {
                                            setMobilePopupRow(null);
                                            if (!isLoggedIn) {
                                                navigate("/login");
                                                return;
                                            }
                                            if (mobilePopupRow.package) {
                                                openPlanModal(mobilePopupRow.package);
                                            }
                                        }}
                                    >
                                        {isLoggedIn ? "Stake" : "Login"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}


                    {showPlanModal && (
                        <div className="mint-modal-overlay" onClick={() => setShowPlanModal(false)}>
                            <div className="mint-modal-content plan-modal" onClick={e => e.stopPropagation()}>
                                <div className="mint-modal-header">
                                    <div className="mint-modal-title">
                                        <img
                                            src={normalizeIconPathString(planModalCurrencyIcon) || `/images/staking_img/${String(planModalCurrency || "usdt").toLowerCase()}_icon.svg`}
                                            alt={planModalCurrency}
                                            className="mint-title-icon"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `/images/staking_img/${String(planModalCurrency || "usdt").toLowerCase()}_icon.svg`;
                                            }}
                                        />
                                        <span>{planModalCurrency} Staking</span>
                                    </div>
                                    <button className="mint-modal-close" onClick={() => setShowPlanModal(false)}><i className="ri-close-line"></i></button>
                                </div>
                                <div className="mint-modal-body mint-plan-modal-body">
                                    {planModalLoading ? (
                                        <div className="mint-plan-loading">Loading plans...</div>
                                    ) : planModalPackages.length === 0 ? (
                                        <div className="mint-plan-empty">No plans available.</div>
                                    ) : (
                                        planModalPackages.map((plan) => (
                                            <div
                                                key={plan._id}
                                                onClick={() => selectPlanAndStake(plan)}
                                                className="mint-plan-card"
                                            >
                                                <div className="mint-plan-card-left">
                                                    <span className="mint-plan-duration">
                                                        {plan.duration} {plan.durationType || 'DAYS'}
                                                    </span>
                                                    <span className="mint-plan-limits">
                                                        Min: {plan.minAmount?.toLocaleString()} — Max: {plan.maxAmount?.toLocaleString()} {plan.currency}
                                                    </span>
                                                </div>
                                                <div className="mint-plan-card-right">
                                                    <span className="mint-plan-apr-val">
                                                        {plan.returnPercentage}%
                                                    </span>
                                                    <span className="mint-plan-apr-lbl">Est. APR</span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showMintModal && (
                        <div className="mint-modal-overlay" onClick={() => setShowMintModal(false)}>
                            <div className="mint-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="mint-modal-header">
                                    <div className="mint-modal-title">
                                        <img src="/images/staking_img/agce_icon.svg" alt="icon" className="mint-title-icon" />
                                        <span>{mintCoin} Mint</span>
                                    </div>
                                    <button className="mint-modal-close" onClick={() => setShowMintModal(false)}><i className="ri-close-line"></i></button>
                                </div>

                                <div className="mint-modal-body">
                                    <div className="mint-modal-left">
                                        <div className="mint-info-row">
                                            <div className="mint-info-item">
                                                <span className="mint-info-label">Protocol</span>
                                                <span className="mint-info-value">{mintCoin}</span>
                                            </div>
                                            <div className="mint-info-item mint-info-item-right">
                                                <span className="mint-info-label">Est. APR <i className="ri-line-chart-line"></i></span>
                                                <span className="mint-info-value mint-green-text">3%</span>
                                            </div>
                                        </div>

                                        <div className="mint-amount-section">
                                            <label className="mint-section-label">Amount</label>
                                            <div className="mint-input-box">
                                                <input type="number" placeholder="Min. 1" value={mintAmount} onChange={e => setMintAmount(e.target.value)} />
                                                <div className="mint-input-addon">
                                                    <span className="mint-max-btn" onClick={() => setMintAmount("1000")}>Max</span>
                                                    <span className="mint-currency-dropdown">USDT <i className="ri-arrow-down-s-fill"></i></span>
                                                </div>
                                            </div>
                                            <div className="mint-amount-details">
                                                <div className="mint-amount-row">
                                                    <span className="mint-amount-lbl">Available</span>
                                                    <span className="mint-amount-val">0 USDT <i className="ri-add-circle-fill mint-add-icon" onClick={() => setAddFundsOpen(true)}></i></span>
                                                </div>
                                                <div className="mint-amount-row">
                                                    <span className="mint-amount-lbl">Individual Minting Cap</span>
                                                    <span className="mint-amount-val">2,000,000 USDT</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mint-apr-section">
                                            <label className="mint-section-label">Est. APR</label>
                                            <div className="mint-apr-box">
                                                <span>Standard APR</span>
                                                <span>3.00%</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mint-modal-right">
                                        <div className="mint-daily-return">
                                            <div className="mint-daily-return-header">
                                                <span>Est. Daily Return</span>
                                                <i className="ri-arrow-up-s-fill mint-arrow-icon"></i>
                                            </div>
                                            <div className="mint-daily-return-box">
                                                <img src="/images/staking_img/agce_icon.svg" alt="icon" className="mint-return-icon" />
                                                <span className="mint-return-val">0 {mintCoin}</span>
                                            </div>
                                        </div>

                                        <div className="mint-rules-section">
                                            <div className="mint-rules-header">
                                                <div className="mint-rules-title">Trading Rules <div className="mint-title-underline"></div></div>
                                            </div>

                                            <div className="mint-timeline">
                                                <div className="mint-timeline-item">
                                                    <div className="mint-timeline-dot active"></div>
                                                    <div className="mint-timeline-content">
                                                        <span className="mint-timeline-lbl">Minting Time</span>
                                                        <span className="mint-timeline-val">Now</span>
                                                    </div>
                                                </div>
                                                <div className="mint-timeline-item">
                                                    <div className="mint-timeline-dot"></div>
                                                    <div className="mint-timeline-content">
                                                        <span className="mint-timeline-lbl">Return Accrues</span>
                                                        <span className="mint-timeline-val">Time Subscribed</span>
                                                    </div>
                                                </div>
                                                <div className="mint-timeline-item">
                                                    <div className="mint-timeline-dot"></div>
                                                    <div className="mint-timeline-content">
                                                        <span className="mint-timeline-lbl">{mintCoin} Distributes</span>
                                                        <span className="mint-timeline-val">Daily</span>
                                                    </div>
                                                </div>
                                                <div className="mint-timeline-item last">
                                                    <div className="mint-timeline-dot icon"><i className="ri-refresh-line"></i></div>
                                                    <div className="mint-timeline-content">
                                                        <span className="mint-timeline-lbl mint-tooltip-wrapper">
                                                            {isFastRedemption ? 'Fast Redemption' : 'Standard Redemption'} <i className="ri-arrow-left-right-line mint-cursor-pointer" onClick={() => setIsFastRedemption(!isFastRedemption)}></i>
                                                            <div className="mint-tooltip">
                                                                0.1% of the redemption amount will be charged as the redemption fee, and the remaining amount is expected to be returned to your Spot Account on 2026-06-06—in about 3 day(s). The {mintCoin} being redeemed will stop generating returns.
                                                            </div>
                                                        </span>
                                                        <div className="mint-timeline-val multi-line">
                                                            Unbonding Period<br />
                                                            About 3 day(s) <i className="ri-arrow-down-s-fill"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mint-modal-footer">
                                    <div className="mint-agreement">
                                        <input type="checkbox" id="mint-agree-cb" checked={mintChecked} onChange={e => setMintChecked(e.target.checked)} />
                                        <label htmlFor="mint-agree-cb">I have read and accepted the <Link to="/staking-user-agreement" onClick={(e) => e.stopPropagation()}>Staking User Agreement</Link></label>
                                    </div>
                                    <div className="mint-actions">
                                        <button className="mint-btn-cancel" onClick={() => setShowMintModal(false)}>Cancel</button>
                                        <button className="mint-btn-confirm" disabled={!mintAmount || !mintChecked} onClick={() => setShowMintModal(false)}>Confirm</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {showStakeModal && selectedPackage && (
                        <StakeModal
                            selectedPackage={selectedPackage}
                            requireOverviewConfirm
                            balanceCache={balanceCacheRef.current}
                            onClose={() => {
                                setShowStakeModal(false);
                                setSelectedPackage(null);
                            }}
                            onTransferClick={openWalletTransfer}
                        />
                    )}


                    {showIntroModal && (
                        <div className="intro-modal-overlay" onClick={() => setShowIntroModal(false)}>
                            <div className="intro-modal-content" onClick={(e) => e.stopPropagation()}>
                                <div className="intro-modal-header">
                                    <button className="intro-back-btn" onClick={() => setShowIntroModal(false)}><i className="ri-arrow-left-s-line"></i></button>
                                    <h2>Introduction</h2>
                                    <button className="intro-close-btn" onClick={() => setShowIntroModal(false)}><i className="ri-close-line"></i></button>
                                </div>

                                <div className="intro-tabs">
                                    <span className="intro-tab active">Overview</span>
                                </div>

                                <div className="intro-modal-body">
                                    <div className="intro-rule-section">
                                        <h4>{mintCoin} Minting Rules</h4>
                                        <p>Stake USDT between 00:00 to 23:59 (UTC) daily to receive {mintCoin} at a conversion rate of 1: 1.</p>
                                        <p>Min Staking Amount for Minting {mintCoin}: 1 USDT</p>
                                        <p>Daily Staking Cap for Minting {mintCoin}: 6,000,000 USDT</p>
                                    </div>

                                    <div className="intro-rule-section">
                                        <h4>{mintCoin} Redemption Rules</h4>
                                        <p>Holders of {mintCoin} can redeem {mintCoin} for USDT/USDC at the rate of 1 {mintCoin} = 1 USDT/USDC. The converted USDT/USDC will be sent to your account after the redemption period ends. The redemption period is dynamically changing based on on-chain conditions.</p>
                                        <p>Min Redemption Amount: 1 {mintCoin}</p>
                                        <p>Daily Redemption Cap: 6,000,000 {mintCoin}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}


                    <AddFundsModal open={addFundsOpen} onClose={() => setAddFundsOpen(false)} onTransferClick={openWalletTransfer} />
                </div>
            </div>

            <Footer />
        </>
    );
};

export default function StakingWrapper() {
    return (
        <FuturesTradeTransferProvider>
            <Staking />
        </FuturesTradeTransferProvider>
    );
}
